/*
  ADA / WCAG 2.2 AA — clean-up for rich-text content pasted into pages, blog posts,
  product descriptions and collection descriptions (Google Docs / ChatGPT / TinyMCE leftovers).

  This is an interim safety net: the same function was used to produce corrected
  HTML for the store's content (phase2/content), which is the permanent fix.
  It only touches markup inside rich-text containers and never changes wording.

  Fixes: 1.3.1 (broken lists, empty/skipped headings, bold pseudo-headings),
         2.4.4 / 4.1.2 (links with no text), 3.2.5-style new-tab warning.
*/
(function (global) {
  'use strict';

  var PSEUDO_HEADING = /^\s*[A-Z0-9][A-Z0-9 &/()'’.,-]{1,40}\s*:?\s*$/;

  function textOf(el) {
    var t = (el.textContent || '').replace(/\u00a0/g, ' ').trim();
    if (t) return t;
    var img = el.querySelector && el.querySelector('img[alt]:not([alt=""])');
    return img ? img.getAttribute('alt').trim() : '';
  }

  function isInternal(a) {
    try {
      var u = new URL(a.getAttribute('href'), 'https://forgefronline.com/');
      return /(^|\.)forgefronline\.com$/.test(u.hostname) || /\.myshopify\.com$/.test(u.hostname);
    } catch (e) { return false; }
  }

  // Readable name for a link destination, e.g. /products/mens-black-balaclava -> "Mens black balaclava".
  // `titles` (optional) maps "products/<handle>" / "collections/<handle>" to real titles.
  function linkLabel(href, titles) {
    var u;
    try { u = new URL(href, 'https://forgefronline.com/'); } catch (e) { return 'Link'; }
    var host = u.hostname.replace(/^www\./, '');
    if (/instagram\.com$/.test(host)) return 'Forge FR on Instagram';
    if (/facebook\.com$/.test(host)) return 'Forge FR on Facebook';
    if (/(^|\.)(x|twitter)\.com$/.test(host)) return 'Forge FR on X (Twitter)';
    if (/youtube\.com$/.test(host)) return 'Forge FR on YouTube';
    if (/pinterest\./.test(host)) return 'Forge FR on Pinterest';
    var parts = u.pathname.split('/').filter(Boolean);
    if (!parts.length) return 'Forge FR home page';
    var key = parts.slice(0, 2).join('/');
    if (titles && titles[key]) return titles[key];
    var words = decodeURIComponent(parts[parts.length - 1]).replace(/[-_]+/g, ' ').replace(/\bcopy( \d+)?$/, '').trim();
    words = words.charAt(0).toUpperCase() + words.slice(1);
    return parts[0] === 'collections' ? words + ' collection' : words;
  }

  function cleanRte(root, opts) {
    opts = opts || {};
    var baseLevel = opts.baseLevel || 1; // level of the page heading that precedes this content
    var doc = root.ownerDocument;
    var log = [];

    // 1. Strip editor artefacts that break semantics
    Array.prototype.forEach.call(root.querySelectorAll('[role="presentation"]'), function (el) {
      if (/^(LI|P|SPAN|UL|OL|H[1-6]|A)$/.test(el.tagName)) { el.removeAttribute('role'); log.push('role=presentation removed'); }
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-start],[data-end],[data-mce-fragment]'), function (el) {
      el.removeAttribute('data-start'); el.removeAttribute('data-end'); el.removeAttribute('data-mce-fragment');
    });

    // 1b. Broken <img> tags with no source (render nothing, but confuse screen readers)
    Array.prototype.forEach.call(root.querySelectorAll('img:not([src]), img[src=""]'), function (img) {
      img.parentNode.removeChild(img); log.push('broken image tag removed');
    });

    // 2. Empty headings -> removed
    Array.prototype.forEach.call(root.querySelectorAll('h1,h2,h3,h4,h5,h6'), function (h) {
      if (!textOf(h)) { h.parentNode.removeChild(h); log.push('empty heading removed'); }
    });

    // 3. Links with no text:
    //    - wrapping an image/icon (visible) -> name it from its destination (never delete visible content)
    //    - completely empty (nothing visible) -> remove
    Array.prototype.forEach.call(root.querySelectorAll('a[href]'), function (a) {
      if (textOf(a) || a.getAttribute('aria-label') || a.getAttribute('title')) return;
      var label = linkLabel(a.getAttribute('href'), opts.titles);
      var img = a.querySelector('img');
      if (img) { img.setAttribute('alt', label); log.push('image link named from destination'); return; }
      if (a.querySelector('svg, picture, video')) { a.setAttribute('aria-label', label); log.push('icon link named from destination'); return; }
      a.parentNode.removeChild(a); log.push('empty link removed');
    });

    // 4. <li> outside a list: unwrap bold/italic wrappers, then wrap runs of stray <li> in <ul>
    Array.prototype.forEach.call(root.querySelectorAll('li'), function (li) {
      var p = li.parentElement;
      if (p && /^(B|STRONG|I|EM|SPAN)$/.test(p.tagName) && p.parentNode) {
        while (p.firstChild) p.parentNode.insertBefore(p.firstChild, p);
        p.parentNode.removeChild(p);
        log.push('list item unwrapped from ' + p.tagName.toLowerCase());
      }
    });
    Array.prototype.forEach.call(root.querySelectorAll('li'), function (li) {
      var p = li.parentElement;
      if (!p || /^(UL|OL|MENU)$/.test(p.tagName) || !li.parentNode) return;
      var ul = doc.createElement('ul');
      p.insertBefore(ul, li);
      var n = li;
      while (n) {
        var next = n.nextSibling;
        if (n.nodeType === 1 && n.tagName === 'LI') ul.appendChild(n);
        else if (n.nodeType === 3 && /^\s*$/.test(n.textContent)) ul.appendChild(n);
        else break;
        n = next;
      }
      log.push('stray list items wrapped in <ul>');
    });

    // 5. Lists may only contain <li>: wrap other element children in <li>
    Array.prototype.forEach.call(root.querySelectorAll('ul,ol'), function (list) {
      Array.prototype.forEach.call(list.children, function (child) {
        if (!/^(LI|SCRIPT|TEMPLATE)$/.test(child.tagName)) {
          if (child.tagName === 'BR' || !textOf(child)) { list.removeChild(child); }
          else { var li = doc.createElement('li'); list.insertBefore(li, child); li.appendChild(child); }
          log.push('non-li child of list fixed');
        }
      });
    });

    // 6. Bold-only paragraphs used as headings ("FEATURES :") -> real headings
    if (opts.pseudoHeadings !== false) {
      Array.prototype.forEach.call(root.querySelectorAll('p'), function (p) {
        var kids = Array.prototype.filter.call(p.childNodes, function (n) { return !(n.nodeType === 3 && /^\s*$/.test(n.textContent)); });
        if (kids.length === 1 && kids[0].nodeType === 1 && /^(STRONG|B)$/.test(kids[0].tagName) && PSEUDO_HEADING.test(kids[0].textContent)) {
          var h = doc.createElement('h' + Math.min(baseLevel + 1, 6));
          h.className = 'ada-rte-heading';
          h.textContent = kids[0].textContent.trim();
          p.parentNode.replaceChild(h, p);
          log.push('bold pseudo-heading -> h' + Math.min(baseLevel + 1, 6));
        }
      });
    }

    // 7. Heading levels: no skips (keep original look via class)
    var prev = baseLevel;
    Array.prototype.forEach.call(root.querySelectorAll('h1,h2,h3,h4,h5,h6'), function (h) {
      var lvl = +h.tagName[1];
      var want = lvl <= baseLevel ? baseLevel + 1 : Math.min(lvl, prev + 1);
      if (want !== lvl) {
        var nh = doc.createElement('h' + want);
        for (var i = 0; i < h.attributes.length; i++) nh.setAttribute(h.attributes[i].name, h.attributes[i].value);
        if (!/\bh[0-6]\b/.test(nh.className)) nh.className = (nh.className + ' h' + lvl).trim(); // same visual size
        while (h.firstChild) nh.appendChild(h.firstChild);
        h.parentNode.replaceChild(nh, h);
        log.push('heading h' + lvl + ' -> h' + want);
        lvl = want;
      }
      prev = lvl;
    });

    // 8. Links that open a new tab: internal -> same tab; external -> announce it
    Array.prototype.forEach.call(root.querySelectorAll('a[target="_blank"]'), function (a) {
      if (isInternal(a)) { a.removeAttribute('target'); log.push('internal link no longer opens new tab'); return; }
      if (!/new (tab|window)/i.test(a.textContent) && !a.querySelector('.ada-new-tab')) {
        var s = doc.createElement('span');
        s.className = 'visually-hidden ada-new-tab';
        s.textContent = ' (opens in a new tab)';
        a.appendChild(s);
        var rel = (a.getAttribute('rel') || '').split(/\s+/);
        if (rel.indexOf('noopener') === -1) rel.push('noopener');
        a.setAttribute('rel', rel.join(' ').trim());
        log.push('external new-tab link announced');
      }
    });

    return log;
  }

  global.adaCleanRte = cleanRte;

  // Theme runtime: clean every rich-text container once the DOM is ready.
  if (typeof document !== 'undefined' && document.currentScript && document.currentScript.dataset.adaRteAuto === 'true') {
    var run = function () {
      // Only real content areas (never header/footer/newsletter text).
      Array.prototype.forEach.call(document.querySelectorAll('.article-template__content, .product__description, .collection-hero__description, .main-page-title ~ .rte, .ada-rte-content'), function (el) {
        if (el.dataset.adaClean) return;
        el.dataset.adaClean = '1';
        // All of these sit directly under the page's H1, so their headings start at H2.
        cleanRte(el, { baseLevel: 1 });
      });
    };
    // Third-party form iframes without a title (e.g. Pify pop-up form) - 4.1.2
    var titleFrames = function () {
      Array.prototype.forEach.call(document.querySelectorAll('iframe[src*="pifyapp.com"]:not([title])'), function (f) {
        f.setAttribute('title', 'Newsletter sign-up form');
      });
    };
    var start = function () {
      run();
      titleFrames();
      if (window.MutationObserver) new MutationObserver(titleFrames).observe(document.body, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  }
})(typeof window !== 'undefined' ? window : this);
